import { Box, Input, Portal } from "@chakra-ui/react";
import { ColorIcon, colors, customEvent, LargeButton, LARGEL_BUTTON_TYPE } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { useRouter } from "next/router";
import { formatPhoneNumber } from "@/utils/formatIdentifyNumber";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import type { CompareVisitItem } from "@/utils/parlorCompareVisitStorage";
import { markVisitedListSubmitted } from "@/utils/parlorCompareVisitStorage";
import { buildParlorVisitedListLeadMessage } from "../utils/parlorVisitedListLeadMessage";
import { LeadConsentRow } from "@/components/LeadConsentRow";

const PRIVACY_CONSENT_URL = "https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d";

const GUESTS = ["무빈소(0명)", "50~100명", "100~200명"] as const;

const MotionBox = motion(Box);
const backdropVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } };
const sheetVariants = { hidden: { y: "100%" }, visible: { y: 0 }, exit: { y: "100%" } };

type Step = "list" | "ask" | "done";

export default function ParlorVisitedListLeadSheet({
  isOpen,
  onClose,
  parlors,
}: {
  isOpen: boolean;
  onClose: () => void;
  parlors: CompareVisitItem[];
}) {
  const [step, setStep] = useState<Step>("list");
  const [guest, setGuest] = useState<string>("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { submitLead } = useSubmitLead();

  const n = parlors.length;
  const canSubmit = Boolean(guest) && /^010-\d{4}-\d{4}$/.test(phone);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep("list");
      setGuest("");
      setPhone("");
      setLoading(false);
    }
  }, [isOpen]);

  const fire = useCallback(
    (
      trigger:
        | "uv_visited_list_mdl"
        | "visited_list-x"
        | "visited_list-bg"
        | "visited_list-done"
        | "visited_list-intro"
        | "visited_list-s1"
        | "visited_list-s2"
        | "visited_list-guest"
        | "visited_list-submit",
      text?: string,
    ) => {
      customEvent({
        newGtm: {
          path: "parlor_detail",
          trigger_and_target: trigger,
          ...(text ? { text } : {}),
        },
      });
    },
    [],
  );

  useEffect(() => {
    if (isOpen) fire("uv_visited_list_mdl");
  }, [isOpen, fire]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || loading || n < 2) return;
    fire("visited_list-s2");
    setLoading(true);
    try {
      await submitLead({
        phone,
        lead_type: "home",
        slack_message: buildParlorVisitedListLeadMessage({
          parlors,
          mournerCount: guest,
        }),
      });
      fire("visited_list-submit");
      markVisitedListSubmitted();
      setStep("done");
    } catch (e) {
      console.error("방문 목록 리드 제출 실패:", e);
      alert("잠시 오류가 발생했습니다. 문제가 지속될 시 고객센터(1666-9784)로 연락주세요.");
    } finally {
      setLoading(false);
    }
  }, [canSubmit, loading, n, phone, parlors, guest, fire, submitLead]);

  const askTitle = guest
    ? `조문객 ${guest} 기준으로, 궁금하신 시설들의 장례 총 비용을 안내해 드려요.`
    : "조문객 수와 번호를 알려주시면, 궁금하신 시설들의 장례 총 비용을 안내해 드려요.";

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            key="visited-list-backdrop"
            position="fixed"
            inset={0}
            zIndex={1500}
            bg="rgba(0,0,0,0.5)"
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            alignItems="center"
            onClick={() => {
              fire("visited_list-bg");
              handleClose();
            }}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <MotionBox
              key="visited-list-sheet"
              onClick={(e: MouseEvent) => e.stopPropagation()}
              width="100%"
              maxWidth="480px"
              bg={colors.white}
              borderTopLeftRadius="20px"
              borderTopRightRadius="20px"
              padding="20px 20px 20px"
              paddingBottom="calc(20px + env(safe-area-inset-bottom, 0px))"
              boxShadow="0 -8px 32px rgba(0,0,0,0.12)"
              maxHeight="86%"
              overflowY="auto"
              variants={sheetVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ type: "spring", damping: 30, stiffness: 340, mass: 0.8 }}
            >
              <BaseStack width="100%" spacing={16}>
                {step !== "done" && (
                  <BaseStack
                    direction="horizontal"
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={12}
                  >
                    <Box
                      className="subtitle_18_b gray900"
                      flex={1}
                      style={{ wordBreak: "keep-all" }}
                    >
                      {step === "ask" ? "조문객 수" : `알아보고 계신 장례식장 ${n}곳`}
                    </Box>
                    <Box
                      cursor="pointer"
                      flex="none"
                      onClick={() => {
                        fire("visited_list-x");
                        handleClose();
                      }}
                      aria-label="닫기"
                    >
                      <ColorIcon name="close" width={26} height={26} color={colors.gray900} />
                    </Box>
                  </BaseStack>
                )}

                {step === "list" && (
                  <>
                    <Box
                      className="body_14_r gray600"
                      style={{ wordBreak: "keep-all", marginTop: -4 }}
                    >
                      아직 결정하시기 전이라면, 비용부터 한번에 안내해 드릴게요.
                    </Box>
                    <VisitRows parlors={parlors} />
                    <LargeButton
                      className="subtitle_16_sb"
                      height={52}
                      borderRadius={12}
                      buttonType={LARGEL_BUTTON_TYPE.ORANGE}
                      onClick={() => {
                        fire("visited_list-s1");
                        setStep("ask");
                      }}
                    >
                      비용 받아보기
                    </LargeButton>
                  </>
                )}

                {step === "ask" && (
                  <>
                    <Box
                      className="body_14_r gray600"
                      style={{ wordBreak: "keep-all", marginTop: -8 }}
                    >
                      {askTitle}
                    </Box>
                    <BaseStack direction="horizontal" width="100%" spacing={8}>
                      {GUESTS.map((g) => {
                        const on = guest === g;
                        return (
                          <Box
                            key={g}
                            as="button"
                            type="button"
                            flex={1}
                            py="10px"
                            px="6px"
                            borderRadius="10px"
                            border={`1px solid ${on ? colors.orange650 : colors.gray200}`}
                            bg={on ? colors.orange50 : colors.white}
                            className="caption_12_b"
                            color={on ? colors.orange650 : colors.gray700}
                            cursor="pointer"
                            onClick={() => {
                              fire("visited_list-guest", g);
                              setGuest(g);
                            }}
                          >
                            {g}
                          </Box>
                        );
                      })}
                    </BaseStack>
                    <Box className="subtitle_18_b gray900">휴대폰 번호</Box>
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                      maxLength={13}
                      type="tel"
                      inputMode="numeric"
                      placeholder="010-0000-0000"
                      className="body_16_r"
                      height="52px"
                      border="none"
                      borderRadius="10px"
                      bg={colors.gray50}
                      color={colors.gray900}
                      px="16px"
                      _placeholder={{ color: colors.gray400 }}
                      _focus={{ outline: "none", boxShadow: `0 0 0 1px ${colors.gray300}` }}
                    />
                    <LeadConsentRow
                      label="개인정보 수집, 이용, 제공 동의"
                      href={PRIVACY_CONSENT_URL}
                    />
                    <LargeButton
                      className="subtitle_16_sb"
                      height={52}
                      borderRadius={12}
                      buttonType={
                        canSubmit && !loading
                          ? LARGEL_BUTTON_TYPE.ORANGE
                          : LARGEL_BUTTON_TYPE.ORANGE_DISABLED
                      }
                      disabled={!canSubmit || loading}
                      onClick={handleSubmit}
                    >
                      {loading ? "제출 중..." : "동의하고 비용 받아보기"}
                    </LargeButton>
                  </>
                )}

                {step === "done" && (
                  <>
                    <Box
                      className="subtitle_18_b gray900"
                      textAlign="center"
                      width="100%"
                      style={{ wordBreak: "keep-all" }}
                    >
                      요청이 접수됐어요
                    </Box>
                    <Box
                      className="body_14_r gray600"
                      textAlign="center"
                      width="100%"
                      style={{ wordBreak: "keep-all" }}
                    >
                      장례전문상담사가 입력하신 번호로
                      <br />
                      조문객 {guest} 기준 {n}곳의 비용을 안내해 드릴게요.
                    </Box>
                    <MiniRows parlors={parlors} />
                    <BaseStack direction="horizontal" width="100%" spacing={8}>
                      <Box flex={1}>
                        <LargeButton
                          className="subtitle_16_sb"
                          height={52}
                          borderRadius={12}
                          buttonType={LARGEL_BUTTON_TYPE.GRAY100}
                          onClick={() => {
                            fire("visited_list-done");
                            handleClose();
                          }}
                        >
                          닫기
                        </LargeButton>
                      </Box>
                      <Box flex={1}>
                        <LargeButton
                          className="subtitle_16_sb"
                          height={52}
                          borderRadius={12}
                          buttonType={LARGEL_BUTTON_TYPE.ORANGE}
                          onClick={() => {
                            fire("visited_list-intro");
                            handleClose();
                            router.push("/funeral-prepare/company-introduction/");
                          }}
                        >
                          고이장례연구소 소개
                        </LargeButton>
                      </Box>
                    </BaseStack>
                  </>
                )}
              </BaseStack>
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>
    </Portal>
  );
}

function VisitRows({ parlors }: { parlors: CompareVisitItem[] }) {
  return (
    <Box>
      {parlors.map((p) => (
        <BaseStack
          key={p.id}
          direction="horizontal"
          alignItems="center"
          spacing={12}
          py="12px"
          style={{ borderBottom: `1px solid ${colors.gray100}` }}
        >
          <Box
            width="52px"
            height="52px"
            borderRadius="10px"
            overflow="hidden"
            bg={colors.gray100}
            flex="none"
          >
            {p.imageUrl ? (
              <Box as="img" src={p.imageUrl} alt="" width="52px" height="52px" objectFit="cover" />
            ) : null}
          </Box>
          <BaseStack spacing={2} flex={1} minWidth={0}>
            <Box className="body_16_m gray900" noOfLines={1}>
              {p.name}
            </Box>
            {p.region ? <Box className="body_14_r gray800">{p.region}</Box> : null}
          </BaseStack>
          {typeof p.minPriceManwon === "number" ? (
            <Box className="caption_12_b" color={colors.orange650} flex="none">
              최소 {p.minPriceManwon.toLocaleString("ko-KR")}만~
            </Box>
          ) : null}
        </BaseStack>
      ))}
    </Box>
  );
}

function MiniRows({ parlors }: { parlors: CompareVisitItem[] }) {
  return (
    <Box bg={colors.gray50} borderRadius="12px" overflow="hidden">
      {parlors.map((p, i) => (
        <BaseStack
          key={p.id}
          direction="horizontal"
          justifyContent="space-between"
          alignItems="center"
          spacing={8}
          padding="10px 12px"
          style={i > 0 ? { borderTop: `1px solid ${colors.gray100}` } : undefined}
        >
          <Box className="caption_12_b gray900" noOfLines={1}>
            {p.name}
          </Box>
          {typeof p.minPriceManwon === "number" ? (
            <Box className="caption_12_b" color={colors.orange650} flex="none">
              최소 {p.minPriceManwon.toLocaleString("ko-KR")}만~
            </Box>
          ) : null}
        </BaseStack>
      ))}
    </Box>
  );
}
